import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  Alert,
  Snackbar,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Search,
  Refresh,
  Payment,
  CheckCircle,
  AttachMoney,
  CreditCard,
  TrendingUp,
} from "@mui/icons-material";
import axios from "axios";
import logger from "../../utils/logger";

const Payments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterMethod, setFilterMethod] = useState("all");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [stats, setStats] = useState({
    total: 0,
    today: 0,
    this_month: 0,
    total_amount: 0,
    today_amount: 0,
    this_month_amount: 0,
  });

  useEffect(() => {
    fetchPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPayments = async () => {
    setLoading(true);
    const token = localStorage.getItem("superAdminToken");
    try {
      const response = await axios.get(
        `${
          process.env.REACT_APP_API_URL || "http://localhost:5000/api"
        }/super-admin/payments`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const paymentsData = response.data.data;
      setPayments(paymentsData);

      // حساب الإحصائيات
      const now = new Date();
      const today = now.toDateString();
      const thisMonth = now.getMonth();
      const thisYear = now.getFullYear();

      const todayPayments = paymentsData.filter(
        (p) => new Date(p.payment_date).toDateString() === today
      );
      const thisMonthPayments = paymentsData.filter((p) => {
        const date = new Date(p.payment_date);
        return date.getMonth() === thisMonth && date.getFullYear() === thisYear;
      });

      setStats({
        total: paymentsData.length,
        today: todayPayments.length,
        this_month: thisMonthPayments.length,
        total_amount: paymentsData.reduce(
          (sum, p) => sum + parseFloat(p.amount || 0),
          0
        ),
        today_amount: todayPayments.reduce(
          (sum, p) => sum + parseFloat(p.amount || 0),
          0
        ),
        this_month_amount: thisMonthPayments.reduce(
          (sum, p) => sum + parseFloat(p.amount || 0),
          0
        ),
      });
    } catch (error) {
      logger.error("Error fetching payments:", error);
      showSnackbar("فشل تحميل المدفوعات", "error");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "success";
      case "pending":
        return "warning";
      case "failed":
        return "error";
      case "refunded":
        return "default";
      default:
        return "default";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "completed":
        return "مكتمل";
      case "pending":
        return "بانتظار التأكيد";
      case "failed":
        return "فاشل";
      case "refunded":
        return "مسترجع";
      default:
        return status;
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const getPaymentMethodText = (method) => {
    const methods = {
      credit_card: "بطاقة ائتمان",
      bank_transfer: "تحويل بنكي",
      cash: "نقدي",
      paypal: "PayPal",
      stripe: "Stripe",
      other: "أخرى",
    };
    return methods[method] || method;
  };

  const filteredPayments = payments.filter((payment) => {
    const matchesSearch =
      payment.tenant_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.reference_number
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());

    const matchesMethod =
      filterMethod === "all" || payment.payment_method === filterMethod;

    return matchesSearch && matchesMethod;
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("ar-IQ", {
      style: "currency",
      currency: "IQD",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" fontWeight="bold">
          المدفوعات
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchPayments}
        >
          تحديث
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    إجمالي المدفوعات
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.total}
                  </Typography>
                </Box>
                <Payment
                  sx={{ fontSize: 50, color: "primary.main", opacity: 0.3 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    مدفوعات اليوم
                  </Typography>
                  <Typography
                    variant="h4"
                    fontWeight="bold"
                    color="success.main"
                  >
                    {stats.today}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatCurrency(stats.today_amount)}
                  </Typography>
                </Box>
                <CheckCircle
                  sx={{ fontSize: 50, color: "success.main", opacity: 0.3 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    هذا الشهر
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="info.main">
                    {stats.this_month}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatCurrency(stats.this_month_amount)}
                  </Typography>
                </Box>
                <TrendingUp
                  sx={{ fontSize: 50, color: "info.main", opacity: 0.3 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    إجمالي المبلغ
                  </Typography>
                  <Typography
                    variant="h5"
                    fontWeight="bold"
                    color="primary.main"
                  >
                    {formatCurrency(stats.total_amount)}
                  </Typography>
                </Box>
                <AttachMoney
                  sx={{ fontSize: 50, color: "primary.main", opacity: 0.3 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              placeholder="البحث في المدفوعات..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>طريقة الدفع</InputLabel>
              <Select
                value={filterMethod}
                label="طريقة الدفع"
                onChange={(e) => setFilterMethod(e.target.value)}
              >
                <MenuItem value="all">الكل</MenuItem>
                <MenuItem value="credit_card">بطاقة ائتمان</MenuItem>
                <MenuItem value="bank_transfer">تحويل بنكي</MenuItem>
                <MenuItem value="cash">نقدي</MenuItem>
                <MenuItem value="paypal">PayPal</MenuItem>
                <MenuItem value="stripe">Stripe</MenuItem>
                <MenuItem value="other">أخرى</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      {loading ? (
        <LinearProgress />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>المرجع</strong>
                </TableCell>
                <TableCell>
                  <strong>المستأجر</strong>
                </TableCell>
                <TableCell>
                  <strong>المبلغ</strong>
                </TableCell>
                <TableCell>
                  <strong>طريقة الدفع</strong>
                </TableCell>
                <TableCell>
                  <strong>التاريخ</strong>
                </TableCell>
                <TableCell>
                  <strong>الفاتورة</strong>
                </TableCell>
                <TableCell>
                  <strong>الحالة</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPayments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary" py={3}>
                      لا توجد مدفوعات
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredPayments.map((payment) => (
                  <TableRow key={payment.id} hover>
                    <TableCell>
                      <Chip
                        label={payment.reference_number || `PAY-${payment.id}`}
                        size="small"
                        variant="outlined"
                        icon={<CreditCard />}
                      />
                    </TableCell>
                    <TableCell>{payment.tenant_name}</TableCell>
                    <TableCell>
                      <Typography fontWeight="bold" color="success.main">
                        {formatCurrency(payment.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getPaymentMethodText(payment.payment_method)}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(payment.payment_date).toLocaleDateString(
                        "ar-IQ",
                        {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        }
                      )}
                    </TableCell>
                    <TableCell>{payment.invoice_number || "-"}</TableCell>
                     <TableCell>
                      <Chip
                        label={getStatusText(payment.status)}
                        color={getStatusColor(payment.status)}
                        size="small"
                        icon={payment.status === 'completed' ? <CheckCircle /> : undefined}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Payments;
